import React from 'react';
import clsx from 'clsx';
import { RotateCcw } from 'lucide-react';
import { PROCESS_ELEMENTS } from '../PfmeaEditorHelpers';

interface PfmeaFiltersProps {
    isDark: boolean;
    showFilters: boolean;
    filter4M: 'all' | 'Man' | 'Machine' | 'Material' | 'Method';
    setFilter4M: (val: 'all' | 'Man' | 'Machine' | 'Material' | 'Method') => void;
    filterPriority: 'all' | 'H' | 'M' | 'L';
    setFilterPriority: (val: 'all' | 'H' | 'M' | 'L') => void;
    filterSC: 'all' | 'CC' | 'SC' | 'none';
    setFilterSC: (val: 'all' | 'CC' | 'SC' | 'none') => void;
}

const PfmeaFilters: React.FC<PfmeaFiltersProps> = ({
    isDark,
    showFilters,
    filter4M,
    setFilter4M,
    filterPriority,
    setFilterPriority,
    filterSC,
    setFilterSC,
}) => {
    if (!showFilters) return null;

    return (
        <div className={clsx(
            'p-4 rounded-xl border flex flex-wrap gap-4 items-center',
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        )}>
            {/* 4M Filter */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">4M Category:</span>
                <div className={clsx('flex rounded-lg p-1', isDark ? 'bg-slate-700' : 'bg-slate-100')}>
                    {['all', ...PROCESS_ELEMENTS.map(e => e.value)].map(cat => (
                        <button
                            key={cat}
                            onClick={() => setFilter4M(cat as any)}
                            className={clsx(
                                'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                                filter4M === cat
                                    ? 'bg-primary-500 text-white'
                                    : 'text-slate-500 hover:text-slate-700'
                            )}
                        >
                            {cat === 'all' ? 'All' : cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Priority Filter */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Priority:</span>
                <div className={clsx('flex rounded-lg p-1', isDark ? 'bg-slate-700' : 'bg-slate-100')}>
                    {['all', 'H', 'M', 'L'].map(p => (
                        <button
                            key={p}
                            onClick={() => setFilterPriority(p as any)}
                            className={clsx(
                                'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                                filterPriority === p
                                    ? p === 'H' ? 'bg-red-500 text-white'
                                        : p === 'M' ? 'bg-amber-500 text-white'
                                            : p === 'L' ? 'bg-green-500 text-white'
                                                : 'bg-primary-500 text-white'
                                    : 'text-slate-500 hover:text-slate-700'
                            )}
                        >
                            {p === 'all' ? 'All' : p}
                        </button>
                    ))}
                </div>
            </div>

            {/* SC/CC Classification Filter */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">SC/CC:</span>
                <div className={clsx('flex rounded-lg p-1', isDark ? 'bg-slate-700' : 'bg-slate-100')}>
                    {(['all', 'CC', 'SC', 'none'] as const).map(sc => (
                        <button
                            key={sc}
                            onClick={() => setFilterSC(sc)}
                            className={clsx(
                                'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                                filterSC === sc
                                    ? sc === 'CC' ? 'bg-red-500 text-white'
                                        : sc === 'SC' ? 'bg-amber-500 text-white'
                                            : sc === 'none' ? 'bg-slate-500 text-white'
                                                : 'bg-primary-500 text-white'
                                    : 'text-slate-500 hover:text-slate-700'
                            )}
                        >
                            {sc === 'all' ? 'All' : sc === 'none' ? 'None' : sc}
                        </button>
                    ))}
                </div>
            </div>

            {/* Clear Filters */}
            {(filter4M !== 'all' || filterPriority !== 'all' || filterSC !== 'all') && (
                <button
                    onClick={() => { setFilter4M('all'); setFilterPriority('all'); setFilterSC('all'); }}
                    className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
                >
                    <RotateCcw size={14} />
                    Clear filters
                </button>
            )}
        </div>
    );
};

export default PfmeaFilters;
